
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import External from 'plaid-threads/Icons/External';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Transfer',
  title: 'Transfer Overview',
  description: 'Intelligently process transfers between accounts'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Callout = makeShortcode("Callout");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Overview.Header product={'Transfer'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Transfer'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/transfer/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='https://github.com/plaid/transfer-quickstart' />
    <Actions.SmallCard icon={<External mdxType="External" />} title='Demo' link='https://plaid.coastdemo.com/share/66d783135f0f8b245d4153fe?zoom=100' />    
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Application Process' link='/docs/transfer/application' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Plaid Transfer (US only) is a flexible multi-rail payment platform designed for companies looking to add or improve their bank payment solution. Transfer provides all of the necessary tools to easily send and manage ACH, RTP, RfP, wire transfer, and FedNow transactions, including:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Easy integration with a single API: Avoid the need for multiple service providers. Connect user accounts, make smarter transaction decisions, manage risk, and move money—all through a single Plaid integration.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Fast settlement, simplified reconciliation: Sweep transaction funds into your treasury account quickly and balance your books with an intuitive reconciliation report.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Multi-rail routing: Dynamic routing between RTP, RfP, and FedNow. Fall back to Same Day ACH if needed. Easily enable new payment rails with a single setting, using the same integration code.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Streamlined operational support: Manage daily operations with dashboards to easily monitor transfer activity.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Payment risk reduction: Minimize your payment failure and ACH return rates by using Plaid's risk engine.`}</p>
      </li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`Plaid Transfer provides a full-service funds transfer solution for payers and recipients within the US. If you prefer to use a third party payment processor, your use case is not supported by Transfer (e.g. a marketplace or money transfer app), or if you or your counterparties are outside the US, see `}<a parentName="p" {...{
          "href": "/docs/auth"
        }}>{`Plaid Auth`}</a>{` instead for a bring-your-own-processor funds transfer solution. See `}<a parentName="p" {...{
          "href": "/docs/payments/#auth-and-transfer-comparison"
        }}>{`Auth and Transfer comparison`}</a>{` for a side-by-side product comparison.`}</p>
      <p>{`For similar end-to-end payment capabilities in Europe, see `}<a parentName="p" {...{
          "href": "/docs/payment-initiation"
        }}>{`Payments (Europe)`}</a>{`.`}</p>
    </Callout>
    <h3 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h3>
    <YouTubeEmbed videoId='GPcW6Htrl5o' alignment='full' title='Plaid Transfer in 3 minutes' caption='Prefer to learn by watching? Watch this introduction to Transfer in 3(-ish) minutes!' mdxType="YouTubeEmbed" />
    <p>{`The process below defines the highest level steps to move money via Transfer and monitor for updates.`}</p>
    <p>{`Prerequisite: Prior to beginning your integration, `}<a parentName="p" {...{
        "href": "/docs/transfer/application/"
      }}>{`complete the Transfer Application`}</a>{` and receive approval. You can begin integrating with Sandbox while waiting for approval.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/transfer/creating-transfers/#account-linking"
          }}>{`Initialize`}</a>{` a Link session to link a consumer bank account`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/transfer/creating-transfers/#authorizing-a-transfer"
          }}>{`Authorize`}</a>{` and `}<a parentName="p" {...{
            "href": "/docs/transfer/creating-transfers/#initiating-a-transfer"
          }}>{`create`}</a>{` the transfer`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) `}<a parentName="p" {...{
            "href": "/docs/transfer/reconciling-transfers/"
          }}>{`Monitor`}</a>{` for updates to the transfer`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) `}<a parentName="p" {...{
            "href": "/docs/transfer/signal-rules"
          }}>{`Use Signal`}</a>{` to enable Signal Transaction Scores and customize authorization rules for your use case `}</p>
      </li>
    </ol>
    <p>{`Additionally, implement additional features such as `}<a parentName="p" {...{
        "href": "/docs/transfer/refunds/"
      }}>{`refunds`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/transfer/recurring-transfers/"
      }}>{`recurring transfers`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/transfer/using-transfer-ui/"
      }}>{`Transfer UI`}</a>{` as needed.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-overview",
        level: 3,
        title: "Integration overview",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
